import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { showLoading, closeSwal, showSaveError, showSaveSuccess, showValidationError } from '@/lib/swal'
import { useAuthStore } from '@/store/auth.store'
import { companyBanksService } from '@/services/companyBanksService'
import type { CityLookup, CompanyBank, CountryLookup, Level5Account } from '@/api/types'

export const CompanyBanksDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const isEditMode = id !== undefined && id !== 'new'

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [accounts, setAccounts] = useState<Level5Account[]>([])
  const [countries, setCountries] = useState<CountryLookup[]>([])
  const [cities, setCities] = useState<CityLookup[]>([])
  const [states, setStates] = useState<CityLookup[]>([])

  const [form, setForm] = useState<CompanyBank>({
    id: 0,
    bankNameEn: '',
    bankNameAr: '',
    accountId: 0,
    primaryPerson: '',
    email: '',
    phoneNo: '',
    address: '',
    city: 0,
    state: 0,
    zipCode: '',
    country: 0,
    activeStatus: true,
    branchId: branchId || 0,
  })

  useEffect(() => {
    if (!branchId) return
    const loadLookups = async () => {
      try {
        setLoading(true)
        const [acc, ctry] = await Promise.all([
          companyBanksService.getLevel5Accounts(branchId),
          companyBanksService.getCountryLookups(),
        ])
        setAccounts(acc ?? [])
        setCountries(ctry ?? [])
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? t('companyBanks.lookupFailed'))
      } finally {
        setLoading(false)
      }
    }
    loadLookups()
  }, [branchId, t])

  useEffect(() => {
    if (!form.country || form.country <= 0) {
      setCities([])
      setStates([])
      return
    }
    const loadCities = async () => {
      try {
        const data = await companyBanksService.getCityLookups(form.country)
        setCities(data ?? [])
      } catch {
        setCities([])
      }
    }
    loadCities()
  }, [form.country])

  useEffect(() => {
    if (!form.city || form.city <= 0) {
      setStates([])
      return
    }
    const loadStates = async () => {
      try {
        const data = await companyBanksService.getStateLookups(form.city)
        setStates(data ?? [])
      } catch {
        setStates([])
      }
    }
    loadStates()
  }, [form.city])

  useEffect(() => {
    if (!isEditMode || !id) return

    const load = async () => {
      try {
        setLoading(true)
        const data = await companyBanksService.getById(Number(id))
        setForm({
          id: data.id ?? Number(id),
          bankNameEn: data.bankNameEn ?? '',
          bankNameAr: data.bankNameAr ?? '',
          accountId: Number(data.accountId ?? 0),
          primaryPerson: data.primaryPerson ?? '',
          email: data.email ?? '',
          phoneNo: data.phoneNo ?? '',
          address: data.address ?? '',
          city: Number(data.city ?? 0),
          state: Number(data.state ?? 0),
          zipCode: data.zipCode ?? '',
          country: Number(data.country ?? 0),
          activeStatus: Boolean(data.activeStatus ?? true),
          branchId: branchId || Number(data.branchId ?? 0),
        })
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? t('companyBanks.loadFailed'))
        navigate('/company-banks')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEditMode, id, navigate, branchId, t])

  const accountOptions: SearchableSelectOption[] = useMemo(() => {
    return (accounts ?? []).map((a) => {
      const idVal = Number(a.id ?? a.accountId ?? 0)
      const label =
        a.accountNameEn ?? a.nameEn ?? a.name ?? a.accountNameAr ?? a.nameAr ?? `${a.accountCode ?? a.code ?? ''}`.trim()
      const prefix = (a.accountCode ?? a.code ?? '').toString().trim()
      return {
        value: String(idVal),
        label: prefix ? `${prefix} - ${label || idVal}` : label || String(idVal),
      }
    })
  }, [accounts])

  const countryOptions: SearchableSelectOption[] = useMemo(
    () => (countries ?? []).map((c) => ({ value: String(c.id), label: c.lookupNameEn || c.lookupNameAr })),
    [countries]
  )

  const cityOptions: SearchableSelectOption[] = useMemo(
    () => (cities ?? []).map((c) => ({ value: String(c.id), label: c.lookupNameEn || c.lookupNameAr })),
    [cities]
  )

  const stateOptions: SearchableSelectOption[] = useMemo(
    () => (states ?? []).map((c) => ({ value: String(c.id), label: c.lookupNameEn || c.lookupNameAr })),
    [states]
  )

  const setField = <K extends keyof CompanyBank>(key: K, value: CompanyBank[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const validate = async () => {
    if (!branchId) return showValidationError(t('companyBanks.branchRequired'))
    if (!form.bankNameEn.trim()) return showValidationError(t('companyBanks.bankNameEnRequired'))
    if (!form.bankNameAr.trim()) return showValidationError(t('companyBanks.bankNameArRequired'))
    if (!form.accountId || form.accountId <= 0) return showValidationError(t('companyBanks.accountRequired'))
    if (!form.primaryPerson.trim()) return showValidationError(t('companyBanks.primaryPersonRequired'))
    if (!form.email.trim()) return showValidationError(t('companyBanks.emailRequired'))
    if (!form.phoneNo.trim()) return showValidationError(t('companyBanks.phoneRequired'))
    if (!form.country || form.country <= 0) return showValidationError(t('companyBanks.countryRequired'))
    if (!form.city || form.city <= 0) return showValidationError(t('companyBanks.cityRequired'))
    if (!form.address.trim()) return showValidationError(t('companyBanks.addressRequired'))
    if (!form.zipCode.trim()) return showValidationError(t('companyBanks.zipRequired'))
    return true
  }

  const handleSave = async () => {
    const ok = await validate()
    if (ok !== true) return

    try {
      setSaving(true)
      showLoading(isEditMode ? t('companyBanks.updating') : t('companyBanks.saving'), t('companyBanks.pleaseWait'))

      const payload: CompanyBank = {
        ...form,
        id: isEditMode ? Number(form.id ?? id) : 0,
        branchId: branchId || form.branchId,
      }

      if (isEditMode) await companyBanksService.update(payload)
      else await companyBanksService.save(payload)

      closeSwal()
      await showSaveSuccess(isEditMode, isEditMode ? t('companyBanks.updated') : t('companyBanks.created'))
      navigate('/company-banks')
    } catch (e: any) {
      closeSwal()
      await showSaveError(e, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  if (loading && !accounts.length && !countries.length) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('companyBanks.title')}
        description={isEditMode ? t('companyBanks.editDescription') : t('companyBanks.newDescription')}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div className="space-y-2">
              <Label>
                {t('companyBanks.bankNameEn')} <span className="text-red-500">*</span>
              </Label>
              <Input value={form.bankNameEn} onChange={(e) => setField('bankNameEn', e.target.value)} disabled={saving} />
            </div>

            <div className="space-y-2">
              <Label>
                {t('companyBanks.bankNameAr')} <span className="text-red-500">*</span>
              </Label>
              <Input
                value={form.bankNameAr}
                onChange={(e) => setField('bankNameAr', e.target.value)}
                disabled={saving}
                dir="rtl"
                className="text-right"
              />
            </div>

            <div className="space-y-2">
              <Label>
                {t('companyBanks.account')} <span className="text-red-500">*</span>
              </Label>
              <SearchableSelect
                options={accountOptions}
                value={form.accountId ? String(form.accountId) : ''}
                onValueChange={(v) => setField('accountId', Number(v))}
                placeholder={t('companyBanks.selectAccount')}
                searchPlaceholder={t('companyBanks.searchAccounts')}
                emptyMessage={t('companyBanks.noAccounts')}
                disabled={saving}
              />
            </div>

            <div className="space-y-2">
              <Label>
                {t('companyBanks.primaryPerson')} <span className="text-red-500">*</span>
              </Label>
              <Input
                value={form.primaryPerson}
                onChange={(e) => setField('primaryPerson', e.target.value)}
                disabled={saving}
              />
            </div>

            <div className="space-y-2">
              <Label>
                {t('companyBanks.email')} <span className="text-red-500">*</span>
              </Label>
              <Input value={form.email} onChange={(e) => setField('email', e.target.value)} disabled={saving} />
            </div>

            <div className="space-y-2">
              <Label>
                {t('companyBanks.phoneNo')} <span className="text-red-500">*</span>
              </Label>
              <Input value={form.phoneNo} onChange={(e) => setField('phoneNo', e.target.value)} disabled={saving} />
            </div>

            <div className="space-y-2">
              <Label>
                {t('companyBanks.country')} <span className="text-red-500">*</span>
              </Label>
              <SearchableSelect
                options={countryOptions}
                value={form.country ? String(form.country) : ''}
                onValueChange={(v) => {
                  setField('country', Number(v))
                  setField('city', 0)
                  setField('state', 0)
                }}
                placeholder={t('companyBanks.selectCountry')}
                searchPlaceholder={t('companyBanks.searchCountries')}
                emptyMessage={t('companyBanks.noCountries')}
                disabled={saving}
              />
            </div>

            <div className="space-y-2">
              <Label>
                {t('companyBanks.city')} <span className="text-red-500">*</span>
              </Label>
              <SearchableSelect
                options={cityOptions}
                value={form.city ? String(form.city) : ''}
                onValueChange={(v) => {
                  setField('city', Number(v))
                  setField('state', 0)
                }}
                placeholder={t('companyBanks.selectCity')}
                searchPlaceholder={t('companyBanks.searchCities')}
                emptyMessage={t('companyBanks.noCities')}
                disabled={saving || !form.country}
              />
            </div>

            <div className="space-y-2">
              <Label>
                {t('companyBanks.state')}
              </Label>
              <SearchableSelect
                options={stateOptions}
                value={form.state ? String(form.state) : ''}
                onValueChange={(v) => setField('state', Number(v))}
                placeholder={t('companyBanks.selectState')}
                searchPlaceholder={t('companyBanks.searchStates')}
                emptyMessage={t('companyBanks.noStates')}
                disabled={saving || !form.city}
              />
            </div>

            <div className="space-y-2">
              <Label>
                {t('companyBanks.zipCode')} <span className="text-red-500">*</span>
              </Label>
              <Input value={form.zipCode} onChange={(e) => setField('zipCode', e.target.value)} disabled={saving} />
            </div>

            <div className="space-y-2 col-span-full">
              <Label>
                {t('companyBanks.address')} <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={form.address}
                onChange={(e) => setField('address', e.target.value)}
                disabled={saving}
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2 col-span-full flex items-center gap-3">
              <Checkbox
                checked={!!form.activeStatus}
                onCheckedChange={(v) => setField('activeStatus', Boolean(v))}
                disabled={saving}
              />
              <Label className="m-0">{t('companyBanks.activeStatus')}</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button type="button" variant="outline" onClick={() => navigate('/company-banks')} disabled={saving}>
          {t('companyBanks.return')}
        </Button>
        <Button onClick={handleSave} disabled={saving || !branchId} className="min-w-[170px]">
          {saving ? (
            <>
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {isEditMode ? t('companyBanks.updating') : t('companyBanks.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isEditMode ? t('common.update') : t('companyBanks.save')}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

